

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.activeElement = null;
      this.sectionId = this.dataset.section || 'cart-drawer';

      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });
      this.addEventListener('click', (event) => {
        if (event.target === this) this.close();
      });

      this.addEventListener(
        'scroll',
        (event) => {
          const list = event.target.closest && event.target.closest('[data-rail-list]');
          if (list) this.updateRailControls(list.closest('[data-rail]'));
        },
        true
      );

      this.bindActions();
      this.bindAddToCartForms();
      this.setHeaderCartIconAccessibility();
      this.exposeLegacyCartApi();

      // The cart can change outside the drawer (cart page, another tab), so the server-rendered
      // markup may already list gift fixes (e.g. a free gift that no longer qualifies).
      setTimeout(() => this.syncGifts([]), 0);
    }

    exposeLegacyCartApi() {
      window.upcartOpenCart = () => this.refresh(true);
      window.upcartRefreshCart = () => this.refresh(false);
      window.openCartDrawer = () => this.open();
      window.refreshCartDrawer = () => this.refresh(false);
    }

    refresh(shouldOpen) {
      const ids = this.getSectionsToRender()
        .map((section) => section.id)
        .join(',');
      const base = (window.routes && window.routes.cart_url) || '/cart';
      return fetch(`${base}?sections=${ids}`)
        .then((response) => response.json())
        .then((sections) => {
          this.renderSections(sections);
          if (shouldOpen) this.open();
          this.syncGifts([]);
        })
        .catch((error) => console.error('[cart-drawer] refresh failed', error));
    }

    bindAddToCartForms() {
      document.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const action = form.getAttribute('action') || '';
        const isAddForm = form.dataset.type === 'add-to-cart-form' || action.indexOf('/cart/add') !== -1;
        if (!isAddForm) return;

        if (form.closest('product-form')) return;

        event.preventDefault();
        this.submitAddForm(form, event.submitter);
      });
    }

    submitAddForm(form, submitter) {
      const spinner = submitter && submitter.querySelector('.loading__spinner');
      if (submitter) {
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('loading');
      }
      if (spinner) spinner.classList.remove('hidden');

      const formData = new FormData(form);
      formData.append('sections', this.getSectionsToRender().map((section) => section.id).join(','));
      formData.append('sections_url', window.location.pathname);

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      this.setActiveElement(submitter || document.activeElement);

      fetch(`${window.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((parsed) => {
          if (parsed.status) {
            console.error('[cart-drawer] add to cart failed:', parsed.description || parsed.message);
            if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'cart-drawer',
                message: parsed.message,
                errors: parsed.errors || parsed.description,
              });
            }
            return;
          }

          const giftId = this.getGiftVariantId(form);
          if (window.addGiftToCart && giftId) {
            return this.ensureGift(giftId).then((giftState) => {
              this.renderContents(giftState || parsed);
              this.publishUpdate(giftState || parsed);
            });
          }

          this.renderContents(parsed);
          this.publishUpdate(parsed);
        })
        .catch((error) => console.error('[cart-drawer] add to cart error', error))
        .finally(() => {
          if (submitter) {
            submitter.removeAttribute('aria-disabled');
            submitter.classList.remove('loading');
          }
          if (spinner) spinner.classList.add('hidden');
        });
    }

    getGiftVariantId(form) {
      const host = form.closest('[data-gift]');
      const id = host && host.dataset.gift;
      return id && id !== '' ? id : null;
    }

    ensureGift(giftId) {
      const body = {
        updates: { [giftId]: 1 },
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      };
      return fetch(`${window.routes.cart_update_url}`, this.fetchOptions(body))
        .then((response) => response.json())
        .catch((error) => {
          console.error('[cart-drawer] gift add failed', error);
          return null;
        });
    }

    publishUpdate(cartData) {
      if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: cartData });
      }
    }

    bindActions() {

      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-action]');
        if (!trigger || trigger === this || !this.contains(trigger)) return;

        const action = trigger.dataset.action;
        const item = trigger.closest('[data-item]');

        switch (action) {
          case 'close':
            event.preventDefault();
            this.close();
            break;
          case 'increase-quantity':
            event.preventDefault();
            this.changeQuantity(item, this.getQuantity(item) + 1);
            break;
          case 'decrease-quantity':
            event.preventDefault();
            this.changeQuantity(item, this.getQuantity(item) - 1);
            break;
          case 'remove-item':
            event.preventDefault();
            if (item.dataset.freeGift) {
              this.removeFreeGift(item);
            } else {
              this.changeQuantity(item, 0);
            }
            break;
          case 'add-product':
            event.preventDefault();
            this.addProduct(trigger.dataset.variant, trigger);
            break;
          case 'toggle-subscribe':
            event.preventDefault();
            this.updateLine(item, {
              selling_plan: trigger.dataset.plan,
              properties: Object.assign(this.getItemProperties(item), { _upgrade: 'true' }),
            });
            break;
          case 'toggle-plans':
            event.preventDefault();
            this.togglePlans(trigger.closest('[data-plan-select]'));
            break;
          case 'select-plan':
            event.preventDefault();
            this.selectPlan(item, trigger);
            break;
          case 'rail-prev':
            event.preventDefault();
            this.scrollRail(trigger, -1);
            break;
          case 'rail-next':
            event.preventDefault();
            this.scrollRail(trigger, 1);
            break;
          case 'to-checkout':
            event.preventDefault();
            window.location.href = `${window.routes.cart_url || '/cart'}/checkout`;
            break;
        }
      });

      this.addEventListener('change', (event) => {
        const input = event.target.closest('input[name="quantity"]');
        if (!input) return;
        const item = input.closest('[data-item]');
        const value = parseInt(input.value, 10);
        this.changeQuantity(item, isNaN(value) ? this.getQuantity(item) : value);
      });

      document.addEventListener('click', (event) => {
        if (!this.classList.contains('active')) return;
        if (event.target.closest('[data-plan-select]')) return;
        this.querySelectorAll('[data-plan-select].is-open').forEach((el) => el.classList.remove('is-open'));
      });
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink || cartLink.dataset.drawerBound === 'true') return;
      cartLink.dataset.drawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);

      requestAnimationFrame(() => {
        this.classList.add('animate', 'active', 'open');
        this.updateRails();
      });

      this.addEventListener(
        'transitionend',
        () => {
          const focusTarget = this.querySelector('.cart-drawer__close') || this.querySelector('.cart-drawer__inner');
          if (typeof trapFocus === 'function' && focusTarget) trapFocus(this, focusTarget);
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.classList.remove('active', 'open');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    getQuantity(item) {
      if (!item) return 1;
      const input = item.querySelector('input[name="quantity"]');
      return input ? parseInt(input.value, 10) || 0 : parseInt(item.dataset.quantity, 10) || 1;
    }

    togglePlans(select) {
      if (!select) return;
      const willOpen = !select.classList.contains('is-open');
      this.querySelectorAll('[data-plan-select].is-open').forEach((el) => el.classList.remove('is-open'));
      select.classList.toggle('is-open', willOpen);
      const toggle = select.querySelector('[data-action="toggle-plans"]');
      if (toggle) toggle.setAttribute('aria-expanded', String(willOpen));
    }

    scrollRail(button, direction) {
      const rail = button.closest('[data-rail]');
      const list = rail && rail.querySelector('[data-rail-list]');
      if (!list) return;
      const card = list.querySelector('.cart-drawer__rail-item');
      const step = card ? card.offsetWidth + 12 : 140;
      list.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    updateRailControls(rail) {
      if (!rail) return;
      const list = rail.querySelector('[data-rail-list]');
      if (!list) return;
      const prev = rail.querySelector('[data-action="rail-prev"]');
      const next = rail.querySelector('[data-action="rail-next"]');
      const max = list.scrollWidth - list.clientWidth;
      if (prev) prev.disabled = list.scrollLeft <= 1;
      if (next) next.disabled = max <= 1 || list.scrollLeft >= max - 1;
    }

    updateRails() {
      this.querySelectorAll('[data-rail]').forEach((rail) => this.updateRailControls(rail));
    }

    changeQuantity(item, quantity) {
      if (!item) return;
      if (quantity < 0) quantity = 0;
      this.updateLine(item, { quantity });
    }

    selectPlan(item, label) {
      if (!item) return;
      const planId = label.dataset.plan;
      const variantId = label.dataset.variant;
      const currentVariant = item.dataset.variant;

      if (variantId && currentVariant && variantId !== currentVariant) {
        this.replaceLine(item, variantId, planId);
      } else {
        this.updateLine(item, { selling_plan: planId });
      }
    }

    getItemProperties(item) {
      const raw = item && item.dataset.properties;
      if (!raw) return {};
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        return {};
      }
      // Liquid serialises line item properties as an array of [key, value] pairs
      const props = {};
      if (Array.isArray(parsed)) {
        parsed.forEach((pair) => {
          if (Array.isArray(pair) && pair.length >= 2) props[pair[0]] = pair[1];
        });
      } else if (parsed && typeof parsed === 'object') {
        Object.assign(props, parsed);
      }
      return props;
    }

    updateLine(item, changes) {
      const body = {
        id: item.dataset.item,
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      };
      if (Object.prototype.hasOwnProperty.call(changes, 'quantity')) body.quantity = changes.quantity;
      if (Object.prototype.hasOwnProperty.call(changes, 'selling_plan')) {
        body.selling_plan = changes.selling_plan || null;
      }
      if (changes.properties) body.properties = changes.properties;

      this.setLineLoading(item, true);
      this.request(window.routes.cart_change_url, body, item);
    }

    // Free gifts are added back automatically while the cart qualifies. When the customer removes
    // one, its variant is remembered in the _declined_gifts cart attribute so it stays removed.
    removeFreeGift(item) {
      const holder = this.querySelector('[data-declined-gifts]');
      let declined = (holder && holder.dataset.declinedGifts) || ',';
      if (declined.indexOf(`,${item.dataset.freeGift},`) === -1) declined += `${item.dataset.freeGift},`;

      const body = {
        updates: { [item.dataset.item]: 0 },
        attributes: { _declined_gifts: declined },
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      };
      this.setLineLoading(item, true);
      this.request(window.routes.cart_update_url, body, item);
    }

    replaceLine(item, variantId, planId) {
      const quantity = this.getQuantity(item);
      const updates = {};
      updates[item.dataset.item] = 0;
      this.setLineLoading(item, true);

      const removeBody = { updates };
      fetch(`${window.routes.cart_update_url}`, this.fetchOptions(removeBody))
        .then((response) => response.json())
        .then(() => {
          const addBody = {
            items: [{ id: Number(variantId), quantity, selling_plan: planId ? Number(planId) : undefined }],
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          };
          return fetch(`${window.routes.cart_add_url}`, this.fetchOptions(addBody)).then((r) => r.json());
        })
        .then((parsed) => this.afterCartChange(parsed))
        .catch((error) => console.error('[cart-drawer] replaceLine failed', error));
    }

    addProduct(variantId, trigger) {
      if (!variantId) return;
      if (trigger) trigger.classList.add('loading');

      const body = {
        items: [{ id: Number(variantId), quantity: 1, properties: { _upsell: 'true' } }],
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      };

      fetch(`${window.routes.cart_add_url}`, this.fetchOptions(body))
        .then((response) => response.json())
        .then((parsed) => this.afterCartChange(parsed))
        .catch((error) => console.error('[cart-drawer] addProduct failed', error))
        .finally(() => trigger && trigger.classList.remove('loading'));
    }

    request(url, body, item) {
      fetch(`${url}`, this.fetchOptions(body))
        .then((response) => response.json())
        .then((parsed) => {
          if (parsed && (parsed.status || parsed.errors || parsed.error)) {
            console.error('[cart-drawer] cart change failed:', parsed.errors || parsed.error || parsed.description || parsed.message);
            if (item) this.setLineLoading(item, false);
            return;
          }
          this.afterCartChange(parsed);
        })
        .catch((error) => {
          console.error('[cart-drawer] request failed', error);
          if (item) this.setLineLoading(item, false);
        });
    }

    fetchOptions(body) {
      const config = typeof fetchConfig === 'function' ? fetchConfig('json') : { method: 'POST', headers: {} };
      config.headers = config.headers || {};
      config.headers['Content-Type'] = 'application/json';
      config.headers.Accept = 'application/json';
      config.body = JSON.stringify(body);
      return config;
    }

    setLineLoading(item, isLoading) {
      if (item) item.classList.toggle('is-loading', isLoading);
    }

    afterCartChange(parsedState, skipGiftSync) {
      const prevGiftIds = this.getGiftIds();
      if (parsedState && parsedState.sections) this.renderSections(parsedState.sections);

      if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: parsedState });
      }

      if (!skipGiftSync) this.syncGifts(prevGiftIds);
    }

    // Gift variants referenced by lines with the _gift_variant property (EG Subscription V7)
    getGiftIds() {
      return [...this.querySelectorAll('[data-item][data-gift-variant]')].map((item) => item.dataset.giftVariant);
    }

    // Adds / removes gifts after a plan change. The drawer markup lists the mismatches in
    // data-gift-updates; gifts whose source line was removed are dropped here. Runs once per change.
    syncGifts(prevGiftIds) {
      // One sync request at a time: its response re-renders the drawer with skipGiftSync.
      if (this.giftSyncInProgress) return;
      const form = this.querySelector('[data-gift-updates]');
      let updates = {};
      if (form) {
        try {
          // Liquid outputs the object body only ('"id":qty,...'); braces break Liquid output tags
          updates = JSON.parse(`{${form.dataset.giftUpdates}}`);
        } catch (error) {
          updates = {};
        }
      }

      const currentGiftIds = this.getGiftIds();
      prevGiftIds.forEach((id) => {
        if (currentGiftIds.includes(id) || id in updates) return;
        if (this.querySelector(`[data-item][data-variant="${id}"]`)) updates[id] = 0;
      });

      if (!Object.keys(updates).length) return;

      const body = {
        updates,
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      };
      this.giftSyncInProgress = true;
      fetch(`${window.routes.cart_update_url}`, this.fetchOptions(body))
        .then((response) => response.json())
        .then((parsed) => {
          if (parsed && parsed.sections) this.afterCartChange(parsed, true);
        })
        .catch((error) => console.error('[cart-drawer] gift sync failed', error))
        .finally(() => {
          this.giftSyncInProgress = false;
        });
    }

    renderSections(sections) {

      if (sections[this.sectionId]) {
        const newDrawer = this.getSectionDOM(sections[this.sectionId], 'cart-drawer');
        if (newDrawer) {

          const prevBar = this.querySelector('[data-progress]');
          const prevWidth = prevBar ? prevBar.style.width : null;

          this.innerHTML = newDrawer.innerHTML;
          if (newDrawer.dataset.count !== undefined) this.dataset.count = newDrawer.dataset.count;
          this.classList.toggle('is-empty', newDrawer.dataset.empty === 'true');

          this.animateProgress(prevWidth);
        }
      }

      if (sections['cart-icon-bubble']) {
        const bubble = document.getElementById('cart-icon-bubble');
        const newBubble = this.getSectionDOM(sections['cart-icon-bubble'], '.shopify-section');
        if (bubble && newBubble) bubble.innerHTML = newBubble.innerHTML;
      }

      requestAnimationFrame(() => this.updateRails());
    }

    animateProgress(prevWidth) {
      const bar = this.querySelector('[data-progress]');
      if (!bar) return;
      const targetWidth = bar.style.width;
      if (prevWidth == null || prevWidth === targetWidth) return;
      bar.style.width = prevWidth;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          bar.style.width = targetWidth;
        });
      });
    }

    getSectionDOM(html, selector) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector(selector) || doc.body;
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return node ? node.innerHTML : '';
    }

    getSectionsToRender() {
      return [
        { id: this.sectionId, selector: 'cart-drawer' },
        { id: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    renderContents(parsedState) {
      this.classList.remove('is-empty');
      if (parsedState && parsedState.sections) this.renderSections(parsedState.sections);
      this.open();
      this.syncGifts([]);
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
